import type { ICustomFilter, ITableFetchOptions } from '../data-table';
import { FilterMethod } from '../data-table/components/filters/filters.models';
import {
    getCachedCodingFtsClaimIds,
    getCachedCommentFtsMessageIds,
    ICodingSearchHit,
    parseCoderRuleNumber,
    TaskSearchIn,
} from './task-search';
import {
    buildMessageIdFilters,
    buildSubjectFilter,
    buildTaskTypeFilters,
    IMessageCategoryRef,
    ITaskListFetchContext,
    TaskTypeId,
} from './task-types';
import { parsePatientIdsFromFilters, PATIENT_ID_FILTER_ID, PATIENT_NAME_FILTER_ID } from './patient-claim-search';
import {
    buildClaimIdScopeFilters,
    CLAIM_CODER_RULE_FILTER_ID,
    CLAIM_PATIENT_FILTER_ID,
    getClaimScopeRateLimiter,
} from './claim-scoped-task-listing';

const sentinel = (filterId: string, value: string): ICustomFilter => ({
    filterId,
    filterMethod: FilterMethod.EQ,
    filterType: 'String',
    value,
});

const LEGACY_MESSAGE_PAYER_FILTER_ID = 'filterPayerId';

export function stripManagedTaskFilters(filters: ICustomFilter[] | undefined): ICustomFilter[] {
    // Only strip filters owned exclusively by task-type chips (open/done inbox).
    // Do NOT strip filterMessageCategoryId / filterUserId — those are also used by
    // Categories / Assignees column filters and must reach the API. Chip category
    // and "My tasks" user filters are applied fresh via buildTaskTypeFilters in
    // mergeTaskListFilters, not via validFilters.
    // Do NOT strip filterSubject / filterId — column Subject/ID filters + searchExtra.
    // filterPatientName becomes the Patient claim-scope sentinel (see buildSearchExtraFilters).
    // Strip leftover filterPatientId too — a persisted chip can hit GET /messages with no UI.
    // The DOS / payer claim-scope sentinels are NOT stripped: the store answers them through
    // claims (claim-scoped-task-listing.ts). The legacy direct `filterPayerId` is — on GET
    // /message it matches only PAYER links, which tasks do not carry, so a persisted one would
    // blank the list (CA-3441).
    return (filters || []).filter(
        f =>
            f.filterId !== 'filterOpen' &&
            f.filterId !== PATIENT_NAME_FILTER_ID &&
            f.filterId !== PATIENT_ID_FILTER_ID &&
            f.filterId !== LEGACY_MESSAGE_PAYER_FILTER_ID,
    );
}

/** Column filters for chip/mine badge counts — ignore the Read dropdown. */
export function stripFiltersForTaskTypeCounts(filters: ICustomFilter[] | undefined): ICustomFilter[] {
    return stripManagedTaskFilters(filters).filter(f => f.filterId !== 'filterRead');
}

function listHasReadFilter(filters: ICustomFilter[] | undefined): boolean {
    return (filters || []).some(f => f.filterId === 'filterRead');
}

/**
 * Resolve search into extra list filters.
 * - subject: single subject LK filter (API paginates normally)
 * - body: FTS on GET /comment → cached message IDs → filterId filters (cache reused across pages)
 * - Patient, coding and rule become claim-scope sentinels: GET /message cannot filter by any of
 *   them, so the store collects the tasks on their claims 100 claims at a time
 *   (claim-scoped-task-listing.ts) — never one URL carrying every claim ID.
 *   - Patient: `filterPatientId` on GET /claim.
 *   - rule: `filterCodingRuleNumber` on GET /claim (FE-126). A term that is not a rule number
 *     returns `'empty'` rather than an unfiltered list that would look filtered.
 *   - coding: FTS on GET /coding (filterResult) → the claim IDs those codings belong to, as an
 *     explicit claim list. Coding-problem tasks link to claims, not codings (verified on a live
 *     account: 50/50 sampled had a `claims` link, 0/50 had a `codings` link).
 *   When a child search returns no hits, return `'empty'` so the table is cleared without
 *   GET /message.
 * - DOS / Payer are not resolved here: they stay in the list filters as claim-scope sentinels.
 *
 * Body resolves to at most 100 message IDs (SEARCH_ID_CAP) and the coding search to at most
 * DEFAULT_MAX_CLAIMS claims — see task-search.ts.
 */
export async function buildSearchExtraFilters(options: {
    searchTerm: string;
    searchIn: TaskSearchIn;
    searchComments: (args: {
        filterBody: string[];
        limit: number;
        offset: number;
    }) => Promise<{ messageId?: string }[]>;
    /**
     * Required for `searchIn: 'coding'`; absent means the app has no coding search wired.
     * Hits must carry `claimId` — the collector keys tasks by claim, not coding id.
     */
    searchCodings?: (args: {
        filterResult: string[];
        limit: number;
        offset: number;
    }) => Promise<ICodingSearchHit[]>;
    listFilters?: ICustomFilter[];
}): Promise<ICustomFilter[] | 'empty'> {
    const extras: ICustomFilter[] = parsePatientIdsFromFilters(options.listFilters).map(id =>
        sentinel(CLAIM_PATIENT_FILTER_ID, id),
    );

    const term = options.searchTerm.trim();
    if (!term) {
        return extras;
    }

    if (options.searchIn === 'subject') {
        extras.push(buildSubjectFilter(term));
        return extras;
    }

    if (options.searchIn === 'coding') {
        if (!options.searchCodings) {
            return extras;
        }
        const codingClaimIds = await getCachedCodingFtsClaimIds({
            query: term,
            searchCodings: options.searchCodings,
            rateLimiter: getClaimScopeRateLimiter(),
        });
        if (codingClaimIds.length === 0) {
            return 'empty';
        }
        return [...extras, ...buildClaimIdScopeFilters(codingClaimIds)];
    }

    if (options.searchIn === 'rule') {
        const ruleNumber = parseCoderRuleNumber(term);
        if (ruleNumber === null) {
            return 'empty';
        }
        return [...extras, sentinel(CLAIM_CODER_RULE_FILTER_ID, String(ruleNumber))];
    }

    const ids = await getCachedCommentFtsMessageIds({
        query: term,
        searchComments: options.searchComments,
    });
    if (ids.length === 0) {
        return 'empty';
    }
    extras.push(...buildMessageIdFilters(ids));
    return extras;
}

/**
 * Badge chip that matches the current list when viewing All or Done.
 * Typed chips (Halts / …) use narrower filters, so their listing total is not the All badge.
 */
export function countedChipIdForContext(typeId: TaskTypeId): TaskTypeId {
    return typeId === 'done' ? 'done' : 'all';
}

/** True when the list request filters match the All/Done badge being counted. */
export function canReuseListingTotalForCountedChip(typeId: TaskTypeId): boolean {
    return typeId === 'all' || typeId === 'done';
}

export async function loadTaskTypeCounts(options: {
    categories: IMessageCategoryRef[];
    context: ITaskListFetchContext;
    currentUserId?: string;
    count: (filters: ICustomFilter[]) => Promise<number>;
    /** Search filters for the active context (same for all counted type chips). */
    searchExtra: () => Promise<ICustomFilter[] | 'empty'>;
    /**
     * Column / table filters from the current list request (Categories, Assignees, …).
     * Managed open/done chip filters are stripped; type-chip filters are applied per counted type.
     */
    listFilters?: ICustomFilter[];
    /**
     * Totals already known from the list response (same filters as that chip).
     * Skips a redundant limit=1 GET /message for those chips.
     */
    knownTotals?: Partial<Record<TaskTypeId, number>>;
    /**
     * `pagination-total` from the list response. When the active view is All/Done and
     * there is no Read filter, this is applied as knownTotals for that chip so we never
     * issue a duplicate GET /message?limit=1&filterOpen=… matching the list.
     */
    listingTotal?: number;
}): Promise<{ typeCounts: Partial<Record<TaskTypeId, number>>; mineCount: number }> {
    const typeCounts: Partial<Record<TaskTypeId, number>> = {};
    /**
     * Counted chips: All (open inbox) and Done. Typed chips (Halts / Denials / …) stay uncounted.
     *
     * BOTH are recounted on every refresh, deliberately. Refreshing only the chip for the current
     * view left the other one showing its pre-filter total, and a body search matching only closed
     * tasks then rendered "All types 0 / Done 57523" over an empty list — which reads as a broken
     * search rather than as "your matches are in Done". The saving was one `limit=1` GET.
     */
    const countedTypeIds: TaskTypeId[] = ['all', 'done'];
    const extra = await options.searchExtra();
    // Badges ignore the Read dropdown; strip it so counts match All/Done chip semantics.
    const columnFilters = stripFiltersForTaskTypeCounts(options.listFilters);
    const known: Partial<Record<TaskTypeId, number>> = { ...(options.knownTotals || {}) };

    // Prefer listing total for the active All/Done chip when the list is not read-filtered.
    if (
        typeof options.listingTotal === 'number' &&
        Number.isFinite(options.listingTotal) &&
        !listHasReadFilter(options.listFilters) &&
        canReuseListingTotalForCountedChip(options.context.typeId)
    ) {
        known[countedChipIdForContext(options.context.typeId)] = options.listingTotal;
    }

    await Promise.all(
        countedTypeIds.map(async typeId => {
            if (typeof known[typeId] === 'number' && Number.isFinite(known[typeId])) {
                typeCounts[typeId] = known[typeId];
                return;
            }
            const typeFilters = buildTaskTypeFilters(typeId, options.categories, {
                mineOnly: options.context.mineOnly,
                currentUserId: options.currentUserId,
            });
            if (extra === 'empty') {
                typeCounts[typeId] = 0;
                return;
            }
            typeCounts[typeId] = await options.count([
                ...columnFilters,
                ...typeFilters,
                ...extra,
            ]);
        }),
    );

    let mineCount = 0;
    const matchingChip = countedChipIdForContext(options.context.typeId);
    if (!options.currentUserId) {
        mineCount = 0;
    } else if (extra === 'empty') {
        mineCount = 0;
    } else if (
        options.context.mineOnly &&
        canReuseListingTotalForCountedChip(options.context.typeId) &&
        typeof known[matchingChip] === 'number'
    ) {
        // List is already My-tasks filtered for All/Done — same total as the Mine badge.
        mineCount = known[matchingChip]!;
    } else {
        const mineFilters = buildTaskTypeFilters(options.context.typeId, options.categories, {
            mineOnly: true,
            currentUserId: options.currentUserId,
        });
        mineCount = await options.count([...columnFilters, ...mineFilters, ...extra]);
    }

    return { typeCounts, mineCount };
}

/**
 * Shared list+counts load with generation guard so stale FTS / listing / count
 * completions cannot overwrite newer results (comment FTS is outside abort groups).
 * Listing is fetched first and applied only if this generation is still current.
 *
 * When comment search resolves to no message IDs (`'empty'`), applies `emptyListing`
 * locally and does not call the messages list API.
 */
export async function runTaskListFetchCycle<TListing extends { total?: number }>(options: {
    generation: { current: number };
    resolveSearchExtra: () => Promise<ICustomFilter[] | 'empty'>;
    /**
     * `onPartial` applies an incomplete listing while a claim-scoped fetch is still collecting
     * (DOS / Payer); it is ignored once this cycle is stale. Ordinary fetches never call it.
     */
    fetchListing: (searchExtra: ICustomFilter[], onPartial: (listing: TListing) => void) => Promise<TListing>;
    applyListing: (listing: TListing) => void;
    /** Used when comment search finds no matches — no GET /message. */
    emptyListing: TListing;
    /**
     * Runs when the search resolves to no matches, so `fetchListing` is never called. A DOS / Payer
     * collection started by an earlier cycle would otherwise keep running — page-level aborts no
     * longer stop it — and the search bar would keep reporting its progress over an empty list.
     */
    onEmptySearch?: () => void;
    /** Receives the listing total so callers can skip duplicate count probes. */
    refreshCounts?: (info: { listingTotal: number }) => Promise<void>;
    shouldRefreshCounts: boolean;
}): Promise<'ok' | 'stale'> {
    const gen = ++options.generation.current;
    const isStale = (): boolean => gen !== options.generation.current;

    const searchExtra = await options.resolveSearchExtra();
    if (isStale()) {
        return 'stale';
    }

    let listingTotal = 0;
    if (searchExtra === 'empty') {
        options.onEmptySearch?.();
        options.applyListing(options.emptyListing);
        listingTotal = Number.isFinite(options.emptyListing.total)
            ? (options.emptyListing.total as number)
            : 0;
    } else {
        const listing = await options.fetchListing(searchExtra, partial => {
            if (!isStale()) {
                options.applyListing(partial);
            }
        });
        if (isStale()) {
            return 'stale';
        }
        options.applyListing(listing);
        listingTotal = Number.isFinite(listing.total) ? (listing.total as number) : 0;
    }

    if (options.shouldRefreshCounts && options.refreshCounts) {
        await options.refreshCounts({ listingTotal });
        if (isStale()) {
            return 'stale';
        }
    }

    return 'ok';
}

export function mergeTaskListFilters(
    fetchData: ITableFetchOptions,
    context: ITaskListFetchContext,
    categories: IMessageCategoryRef[],
    currentUserId: string | undefined,
    searchExtra: ICustomFilter[],
): ICustomFilter[] {
    const typeFilters = buildTaskTypeFilters(context.typeId, categories, {
        mineOnly: context.mineOnly,
        currentUserId,
    });
    const other = stripManagedTaskFilters(fetchData.validFilters);
    return [...other, ...typeFilters, ...searchExtra];
}

export { buildCodingResultFtsValue, buildCommentBodyFtsValue } from './task-search';
